import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, Body3Regular, CaptionLight } from '../../lib/font';
import {
    useTeam,
    useAddTeamMember,
    useRemoveTeamMember,
    useChangeTeamLeader,
    useUpdateTeam,
    useCenterSalesManagers,
    useRebackfillTeam,
} from '@/features/team';
import type { CenterSalesManager, TeamMembership } from '@/features/team';
import { salesManagerToUserType } from '@/features/team';

const formatKRW = (n: number) => `${(n ?? 0).toLocaleString()}원`;

const TeamDetailPage = ({ createAlert }: { createAlert: any }) => {
    const navigate = useNavigate();
    const { teamId = '' } = useParams<{ teamId: string }>();
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId: string = authObject?.centerId;

    const { data, isLoading } = useTeam({ centerId, teamId });
    const addMutation = useAddTeamMember(centerId, teamId);
    const removeMutation = useRemoveTeamMember(centerId, teamId);
    const leaderMutation = useChangeTeamLeader(centerId, teamId);
    const updateMutation = useUpdateTeam(centerId, teamId);
    const { data: smData } = useCenterSalesManagers({ centerId });
    const allManagers = smData?.all ?? [];
    const activeManagers = smData?.active ?? [];
    const rebackfillMutation = useRebackfillTeam(centerId, teamId);

    // 백필 재실행 모달
    const [isRebackfillOpen, setIsRebackfillOpen] = useState(false);
    const [rebackfillFrom, setRebackfillFrom] = useState('');

    // 팀명/설명 편집
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDesc, setEditDesc] = useState('');

    // 팀원 추가 (강사+판매담당 통합)
    const [addUserId, setAddUserId] = useState('');
    const [addAllowTransfer, setAddAllowTransfer] = useState(false);

    // 제거 확인
    const [removeTarget, setRemoveTarget] = useState<TeamMembership | null>(null);

    if (isLoading || !data) {
        return (
            <Col style={{ padding: 32 }}>
                <Body2Regular style={{ color: colors.GREY_300 }}>로딩 중...</Body2Regular>
            </Col>
        );
    }

    const { team, members, monthRevenue } = data;

    // 이름 표시용 매핑 (퇴사자 포함) + 추가 가능 옵션 (활성 + 미가입자)
    const userNameById = new Map<string, string>(allManagers.map((m) => [m.userId, m.userName]));
    const memberUserIds = new Set(members.map((m) => m.userId));
    const availableManagers = activeManagers.filter((m) => !memberUserIds.has(m.userId));

    const handleStartEdit = () => {
        setEditName(team.name);
        setEditDesc(team.description ?? '');
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        try {
            await updateMutation.mutateAsync({ name: editName.trim(), description: editDesc });
            createAlert('수정 완료', '팀 정보가 수정되었습니다.', false);
            setIsEditing(false);
        } catch (e: any) {
            createAlert('수정 실패', e?.message ?? '서버 오류', true);
        }
    };

    const handleAdd = async () => {
        if (!addUserId) {
            createAlert('입력 오류', '추가할 사람을 선택하세요.', true);
            return;
        }
        const target = activeManagers.find((m) => m.userId === addUserId);
        if (!target) {
            createAlert('입력 오류', '대상 정보를 찾을 수 없습니다.', true);
            return;
        }
        try {
            const res = await addMutation.mutateAsync({
                userId: target.userId,
                userType: salesManagerToUserType(target.role),
                allowTransfer: addAllowTransfer,
            });
            const transferred = res.data?.transferredFrom;
            createAlert(
                '팀원 추가 완료',
                transferred ? '다른 팀에서 이동되었습니다.' : '팀원이 추가되었습니다.',
                false,
            );
            setAddUserId('');
            setAddAllowTransfer(false);
        } catch (e: any) {
            createAlert('추가 실패', e?.message ?? '서버 오류', true);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        try {
            await removeMutation.mutateAsync(removeTarget.userId);
            createAlert('제거 완료', '팀원이 제거되었습니다.', false);
            setRemoveTarget(null);
        } catch (e: any) {
            createAlert('제거 실패', e?.message ?? '서버 오류', true);
        }
    };

    const handleRebackfill = async () => {
        try {
            const res = await rebackfillMutation.mutateAsync({
                effectiveFrom: rebackfillFrom ? new Date(rebackfillFrom).toISOString() : null,
            });
            const bf = res.data?.backfillResult;
            const updated =
                (bf?.memberTicketPayment?.updated ?? 0) + (bf?.membershipPayment?.updated ?? 0);
            const skipped =
                (bf?.memberTicketPayment?.skipped ?? 0) + (bf?.membershipPayment?.skipped ?? 0);
            createAlert(
                '백필 완료',
                `${updated}건의 매출이 이 팀에 추가 박제되었습니다.${
                    skipped > 0 ? ` (이미 다른 팀에 박제된 ${skipped}건은 건너뜀)` : ''
                }`,
                false,
            );
            setIsRebackfillOpen(false);
            setRebackfillFrom('');
        } catch (e: any) {
            createAlert('백필 실패', e?.message ?? '서버 오류', true);
        }
    };

    const handleMakeLeader = async (m: TeamMembership) => {
        try {
            await leaderMutation.mutateAsync({ userId: m.userId, userType: m.userType });
            createAlert('팀장 교체 완료', '팀장이 교체되었습니다.', false);
        } catch (e: any) {
            createAlert('팀장 교체 실패', e?.message ?? '서버 오류', true);
        }
    };

    return (
        <Col style={{ width: '100%', padding: '112px 48px 48px', gap: 28 }}>
            {/* 헤더 */}
            <Row style={{ alignItems: 'center', gap: 12 }}>
                <button
                    onClick={() => navigate('/teams')}
                    style={{
                        padding: '8px 14px',
                        border: `1px solid ${colors.GREY_50}`,
                        background: colors.white,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 14,
                        color: colors.GREY_400,
                    }}
                >
                    ← 팀 목록
                </button>
            </Row>

            {/* 팀 정보 */}
            <Col
                style={{
                    padding: 28,
                    background: colors.white,
                    border: `1px solid ${colors.GREY_50}`,
                    borderRadius: 10,
                    gap: 16,
                }}
            >
                {!isEditing ? (
                    <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Col style={{ gap: 4 }}>
                            <Row style={{ gap: 8, alignItems: 'center' }}>
                                <Subtitle1Bold style={{ color: colors.GREY_900 }}>{team.name}</Subtitle1Bold>
                                {!team.isActive && (
                                    <CaptionLight style={{ color: colors.GREY_200 }}>(해체됨)</CaptionLight>
                                )}
                            </Row>
                            {team.description && (
                                <Body3Regular style={{ color: colors.GREY_300 }}>{team.description}</Body3Regular>
                            )}
                            <Body2Regular style={{ color: colors.GREY_400 }}>
                                팀장: {userNameById.get(team.leaderUserId) ?? team.leaderUserId}{' '}
                                <span style={{ color: colors.GREY_300, fontSize: 14 }}>
                                    ({team.leaderUserType === 'coach' ? '강사' : '판매담당'})
                                </span>
                            </Body2Regular>
                        </Col>
                        {team.isActive && (
                            <Row style={{ gap: 8 }}>
                                <SecondaryButton
                                    onClick={() => setIsRebackfillOpen(true)}
                                    textColor={colors.GREY_400}
                                >
                                    이전 매출 다시 합산
                                </SecondaryButton>
                                <SecondaryButton onClick={handleStartEdit} textColor={colors.PRIMARY_BLUE}>
                                    팀 정보 수정
                                </SecondaryButton>
                            </Row>
                        )}
                    </Row>
                ) : (
                    <Col style={{ gap: 16 }}>
                        <Col style={{ gap: 6 }}>
                            <Body3Regular style={{ color: colors.GREY_400 }}>팀명</Body3Regular>
                            <input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                style={{
                                    padding: '12px 14px',
                                    border: `1px solid ${colors.GREY_50}`,
                                    borderRadius: 6,
                                    fontSize: 16,
                                }}
                            />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <Body3Regular style={{ color: colors.GREY_400 }}>설명</Body3Regular>
                            <input
                                value={editDesc}
                                onChange={(e) => setEditDesc(e.target.value)}
                                style={{
                                    padding: '12px 14px',
                                    border: `1px solid ${colors.GREY_50}`,
                                    borderRadius: 6,
                                    fontSize: 16,
                                }}
                            />
                        </Col>
                        <Row style={{ gap: 8, justifyContent: 'flex-end' }}>
                            <SecondaryButton onClick={() => setIsEditing(false)}>취소</SecondaryButton>
                            <PrimaryButton onClick={handleSaveEdit} disabled={updateMutation.isLoading}>
                                {updateMutation.isLoading ? '저장 중...' : '저장'}
                            </PrimaryButton>
                        </Row>
                    </Col>
                )}

                <Row style={{ gap: 32, marginTop: 16 }}>
                    <Col style={{ gap: 6 }}>
                        <Body3Regular style={{ color: colors.GREY_300 }}>이번 달 매출</Body3Regular>
                        <Subtitle1Bold style={{ color: colors.PRIMARY_BLUE }}>
                            {formatKRW(monthRevenue?.total ?? 0)}
                        </Subtitle1Bold>
                    </Col>
                    <Col style={{ gap: 6 }}>
                        <Body3Regular style={{ color: colors.GREY_300 }}>환불</Body3Regular>
                        <Body2Regular style={{ color: colors.red }}>
                            {formatKRW(monthRevenue?.refund ?? 0)}
                        </Body2Regular>
                    </Col>
                </Row>
            </Col>

            {/* 팀원 추가 */}
            {team.isActive && (
                <Col
                    style={{
                        padding: 20,
                        background: colors.Gray,
                        border: `1px dashed ${colors.GREY_50}`,
                        borderRadius: 8,
                        gap: 10,
                    }}
                >
                    <Body3Regular style={{ color: colors.GREY_400 }}>팀원 추가 (강사 + 판매담당자)</Body3Regular>
                    <Row style={{ gap: 10, alignItems: 'center' }}>
                        <select
                            value={addUserId}
                            onChange={(e) => setAddUserId(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '12px 14px',
                                border: `1px solid ${colors.GREY_50}`,
                                borderRadius: 6,
                                fontSize: 16,
                            }}
                        >
                            <option value="">— 추가할 사람 선택 —</option>
                            {availableManagers.map((m: CenterSalesManager) => (
                                <option key={m.userId} value={m.userId}>
                                    {m.userName} {m.role === 'coach' ? '(강사)' : '(판매)'}
                                </option>
                            ))}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, color: colors.GREY_400 }}>
                            <input
                                type="checkbox"
                                checked={addAllowTransfer}
                                onChange={(e) => setAddAllowTransfer(e.target.checked)}
                            />
                            다른 팀에서 이동
                        </label>
                        <PrimaryButton onClick={handleAdd} disabled={addMutation.isLoading || !addUserId}>
                            추가
                        </PrimaryButton>
                    </Row>
                    {availableManagers.length === 0 && activeManagers.length > 0 && (
                        <Body3Regular style={{ color: colors.GREY_300 }}>
                            이 센터의 모든 강사/판매담당자가 이미 이 팀에 있습니다.
                        </Body3Regular>
                    )}
                </Col>
            )}

            {/* 팀원 목록 */}
            <Col style={{ gap: 12 }}>
                <Subtitle1Bold style={{ color: colors.GREY_900 }}>팀원 ({members.length}명)</Subtitle1Bold>
                <Col style={{ gap: 8 }}>
                    {members.map((m) => (
                        <Row
                            key={m._id}
                            style={{
                                padding: '16px 18px',
                                background: colors.white,
                                border: `1px solid ${colors.GREY_50}`,
                                borderRadius: 8,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Row style={{ gap: 14, alignItems: 'center' }}>
                                {m.role === 'leader' && (
                                    <span
                                        style={{
                                            padding: '4px 10px',
                                            background: colors.PRIMARY_BLUE,
                                            color: colors.white,
                                            borderRadius: 12,
                                            fontSize: 13,
                                            fontWeight: 600,
                                        }}
                                    >
                                        팀장
                                    </span>
                                )}
                                <Body2Regular style={{ color: colors.GREY_900 }}>
                                    {userNameById.get(m.userId) ?? m.userId}
                                </Body2Regular>
                                <span
                                    style={{
                                        padding: '3px 10px',
                                        background: m.userType === 'coach' ? colors.BLUE_50 : colors.WHITE_300,
                                        color: m.userType === 'coach' ? colors.PRIMARY_BLUE : colors.GREY_400,
                                        borderRadius: 10,
                                        fontSize: 13,
                                    }}
                                >
                                    {m.userType === 'coach' ? '강사' : '판매'}
                                </span>
                            </Row>
                            <Row style={{ gap: 16, alignItems: 'center' }}>
                                <Col style={{ alignItems: 'flex-end', gap: 2 }}>
                                    <CaptionLight style={{ color: colors.GREY_300 }}>
                                        이번 달 매출
                                    </CaptionLight>
                                    <Body2Regular
                                        style={{
                                            color:
                                                (m.monthRevenue?.total ?? 0) > 0
                                                    ? colors.PRIMARY_BLUE
                                                    : colors.GREY_300,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {formatKRW(m.monthRevenue?.total ?? 0)}
                                    </Body2Regular>
                                    {(m.monthRevenue?.refund ?? 0) > 0 && (
                                        <CaptionLight style={{ color: colors.red }}>
                                            환불 {formatKRW(m.monthRevenue?.refund ?? 0)}
                                        </CaptionLight>
                                    )}
                                </Col>
                                {team.isActive && m.role !== 'leader' && (
                                    <Row style={{ gap: 8 }}>
                                        <SecondaryButton
                                            onClick={() => handleMakeLeader(m)}
                                            textColor={colors.PRIMARY_BLUE}
                                        >
                                            팀장으로
                                        </SecondaryButton>
                                        <SecondaryButton
                                            onClick={() => setRemoveTarget(m)}
                                            textColor={colors.red}
                                        >
                                            제거
                                        </SecondaryButton>
                                    </Row>
                                )}
                            </Row>
                        </Row>
                    ))}
                </Col>
            </Col>

            {/* 백필 재실행 모달 */}
            {isRebackfillOpen && (
                <div
                    onClick={() => setIsRebackfillOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: colors.white,
                            borderRadius: 12,
                            padding: 32,
                            width: 520,
                        }}
                    >
                        <Subtitle1Bold style={{ color: colors.GREY_900 }}>
                            이전 매출 다시 합산
                        </Subtitle1Bold>
                        <Body2Regular
                            style={{ color: colors.GREY_400, marginTop: 12, display: 'block', lineHeight: 1.6 }}
                        >
                            지정한 날짜 이후 팀원이 판매한 결제 중 <b>아직 어느 팀에도 박제되지 않은 매출</b>을
                            이 팀에 합산합니다.
                            <br />
                            <span style={{ fontSize: 13, color: colors.GREY_300 }}>
                                ※ 이미 다른 팀에 박제된 매출은 보호되어 건너뜁니다.
                            </span>
                        </Body2Regular>
                        <Col style={{ gap: 6, marginTop: 20 }}>
                            <Body3Regular style={{ color: colors.GREY_400 }}>
                                시작일 (미입력 시 이번 달 1일)
                            </Body3Regular>
                            <input
                                type="date"
                                value={rebackfillFrom}
                                onChange={(e) => setRebackfillFrom(e.target.value)}
                                style={{
                                    padding: '12px 14px',
                                    border: `1px solid ${colors.GREY_50}`,
                                    borderRadius: 6,
                                    fontSize: 16,
                                }}
                            />
                        </Col>
                        <Row style={{ gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                            <SecondaryButton onClick={() => setIsRebackfillOpen(false)}>취소</SecondaryButton>
                            <PrimaryButton
                                onClick={handleRebackfill}
                                disabled={rebackfillMutation.isLoading}
                            >
                                {rebackfillMutation.isLoading ? '처리 중...' : '다시 합산'}
                            </PrimaryButton>
                        </Row>
                    </div>
                </div>
            )}

            {/* 제거 확인 */}
            {removeTarget && (
                <ConfirmModal
                    title="팀원 제거"
                    description={`${userNameById.get(removeTarget.userId) ?? removeTarget.userId} 님을 팀에서 제거합니다.`}
                    onConfirm={handleRemove}
                    onCancel={() => setRemoveTarget(null)}
                    confirmText="제거"
                />
            )}
        </Col>
    );
};

const PrimaryButton = ({
    children,
    onClick,
    disabled,
}: {
    children: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    disabled?: boolean;
}) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            padding: '12px 22px',
            background: disabled ? colors.WHITE_700 : colors.PRIMARY_BLUE,
            color: colors.white,
            border: 'none',
            borderRadius: 6,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 16,
            fontWeight: 600,
        }}
    >
        {children}
    </button>
);

const SecondaryButton = ({
    children,
    onClick,
    textColor = colors.GREY_400,
}: {
    children: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    textColor?: string;
}) => (
    <button
        onClick={onClick}
        style={{
            padding: '10px 18px',
            background: colors.white,
            color: textColor,
            border: `1px solid ${colors.GREY_50}`,
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 500,
        }}
    >
        {children}
    </button>
);

const ConfirmModal = ({
    title,
    description,
    onConfirm,
    onCancel,
    confirmText = '확인',
}: {
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
}) => (
    <div
        onClick={onCancel}
        style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: colors.white, borderRadius: 12, padding: 32, width: 460 }}
        >
            <Subtitle1Bold style={{ color: colors.GREY_900 }}>{title}</Subtitle1Bold>
            <Body2Regular style={{ color: colors.GREY_400, marginTop: 16, display: 'block' }}>
                {description}
            </Body2Regular>
            <Row style={{ gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
                <PrimaryButton onClick={onConfirm}>{confirmText}</PrimaryButton>
            </Row>
        </div>
    </div>
);

export default TeamDetailPage;
